import { useCallback, useEffect, useState } from 'react';
import { SelectForm } from '../../components/select-form/select-form';
import { DateTime, Interval } from 'luxon';
import { DatePicker } from '../../components/date-picker/date-picker';
import { getFromToStrings } from './date-time-utils';
import { Modal } from '../../components/modal/modal';
import './date-time-advanced.scss';
import { SingleValue } from 'react-select';
import { Button } from '../../components/form/button';

type DateSelect = {
  label: string;
  value: string;
};

enum DateSelectOptions {
  TODAY = 'today',
  TOMORROW = 'tomorrow',
  CURRENT_WEEK = 'cWeek',
  SEVEN_DAYS = 'week',
  CUSTOM = 'custom',
}

const dateSelectPattern = (label: string) => {
  switch (label) {
    case DateSelectOptions.TODAY:
      return {
        fromGetter: () => DateTime.now().startOf('day'),
        toGetter: () => DateTime.now().endOf('day'),
      };
    case DateSelectOptions.TOMORROW:
      return {
        fromGetter: () => DateTime.now().plus({ days: 1 }).startOf('day'),
        toGetter: () => DateTime.now().plus({ days: 1 }).endOf('day'),
      };
    case DateSelectOptions.CURRENT_WEEK:
      return {
        fromGetter: () => DateTime.now().startOf('week'),
        toGetter: () => DateTime.now().endOf('week'),
      };
    case DateSelectOptions.SEVEN_DAYS:
      return {
        fromGetter: () => DateTime.now().startOf('day'),
        toGetter: () => DateTime.now().plus({ days: 7 }).endOf('day'),
      };
    default:
      return {
        fromGetter: () => DateTime.now().startOf('day'),
        toGetter: () => DateTime.now().endOf('day'),
      };
  }
};

const optionsLabels = [
  { value: DateSelectOptions.TODAY, label: 'Today' },
  { value: DateSelectOptions.TOMORROW, label: 'Tomorrow' },
  { value: DateSelectOptions.CURRENT_WEEK, label: 'Week to Date' },
  { value: DateSelectOptions.SEVEN_DAYS, label: 'Next 7 Days' },
  { value: DateSelectOptions.CUSTOM, label: 'Custom' },
];

const today = optionsLabels[0];

export type DateTimeQueryParams = {
  from?: DateTime | null;
  to?: DateTime | null;
  timeRange?: string;
};

export const DateTimeAdvanced = ({
  setFrom,
  setTo,
  request,
  setRequest,
}: {
  setFrom: (dt: DateTime | null) => void;
  setTo: (dt: DateTime | null) => void;
  request: DateTimeQueryParams;
  setRequest: React.Dispatch<React.SetStateAction<DateTimeQueryParams>>;
}) => {
  // State
  const [value, setValue] = useState<DateSelect | null>(null);
  const [modal, setModal] = useState<boolean>(false);
  const [labelFrom, setLabelFrom] = useState<DateTime | null>(null);
  const [labelTo, setLabelTo] = useState<DateTime | null>(null);

  const setParamsFromChanges = useCallback(
    (request: DateTimeQueryParams) => {
      const { fromGetter, toGetter } = dateSelectPattern(request.timeRange ?? DateSelectOptions.CUSTOM);

      const foundTimeRange = request.timeRange && optionsLabels.find((option) => option.value === request.timeRange);

      // Custom label in this case
      if (!foundTimeRange) {
        setFrom(request.from ?? null);
        setTo(request.to ?? DateTime.now().endOf('day'));
        setLabelFrom(request.from ?? null);
        setLabelTo(request.to ?? null);
      } else {
        setFrom(fromGetter());
        setTo(toGetter());
      }

      setValue(
        request.timeRange
          ? optionsLabels.find((option) => option.value === request.timeRange) ?? {
              label: request.timeRange,
              value: DateSelectOptions.CUSTOM,
            }
          : optionsLabels[0],
      );
    },
    [setFrom, setTo, setValue],
  );

  useEffect(() => {
    setParamsFromChanges(request);
  }, [request.timeRange]);

  const onApply = (f: DateTime | null, t: DateTime | null) => {
    const dateStr = getFromToStrings(f, t);

    // Not allowing blank time for now without pagination
    if (dateStr === 'Before Today') {
      setValue(today);
      setRequest((prev) => ({ ...prev, timeRange: today.value, from: null, to: null }));
    } else {
      setValue({ label: dateStr, value: DateSelectOptions.CUSTOM });
      setRequest((prev) => ({ ...prev, timeRange: dateStr, from: f, to: t }));
    }
  };

  const onClose = () => {
    setModal(false);
  };

  const onOptionChange = (v: SingleValue<DateSelect>) => {
    if (v!.value !== DateSelectOptions.CUSTOM) {
      setValue(v);
      setRequest((prev) => ({ ...prev, timeRange: v!.value, from: null, to: null }));
    } else {
      setModal(true);
    }
  };

  return (
    <>
      <Modal isOpen={modal} close={onClose} title="Select a date range">
        <PrettyFromToDate
          setFrom={setLabelFrom}
          setTo={setLabelTo}
          from={labelFrom ?? undefined}
          to={labelTo ?? undefined}
          onClose={onClose}
          onApply={onApply}
        />
      </Modal>
      <SelectForm
        options={optionsLabels}
        value={value}
        onChange={onOptionChange}
        styles={{
          input: (baseStyles) => ({
            ...baseStyles,
            padding: 0,
            margin: 0,
          }),
        }}
        placeholder="Select a date range"
      />
    </>
  );
};

const PrettyFromToDate = ({
  setFrom,
  setTo,
  from,
  to,
  onClose,
  onApply,
}: {
  setFrom: (l: DateTime | null) => void;
  setTo: (l: DateTime | null) => void;
  from: DateTime | undefined;
  to: DateTime | undefined;
  onClose: () => void;
  onApply: (f: DateTime | null, t: DateTime | null) => void;
}) => {
  const [tempFrom, setTempFrom] = useState<DateTime | undefined>(from);
  const [tempTo, setTempTo] = useState<DateTime | undefined>(to);

  const onApplyModal = () => {
    const newFrom = tempFrom ? tempFrom.startOf('day') : null;
    const newTo = tempTo ? tempTo.endOf('day') : null;
    setFrom(newFrom);
    setTo(newTo);
    onApply(newFrom, newTo);
    onClose();
  };

  return (
    <div>
      <div className="date-selection-wrapper grid gap-1 grid-rows-1 grid-cols-1 md:grid-cols-2 md:grid-rows-2 w-max">
        <DatePicker
          value={tempFrom}
          clearable={true}
          onChange={(tempFrom) => setTempFrom(tempFrom)}
          forceOpen={true}
          disabledIntervals={[Interval.after(DateTime.now().plus({ minutes: 30 }), { years: 10 })]}
        />
        <DatePicker
          value={tempTo}
          clearable={true}
          onChange={(tempTo) => setTempTo(tempTo)}
          forceOpen={true}
          disabledIntervals={from && [Interval.fromDateTimes(DateTime.fromMillis(0), from)]}
        />
      </div>
      <div className="date-selection-footer flex flex-row justify-end gap-2">
        <Button className="button-secondary" onClick={onClose}>
          <p className="modal-button">Cancel</p>
        </Button>
        <Button className="button-primary" onClick={onApplyModal}>
          <p className="modal-button">Apply</p>
        </Button>
      </div>
    </div>
  );
};
